import React, {useEffect, useMemo, useState, useRef} from "react";
import {useExpanded,  usePagination, useResizeColumns, useSortBy, useTable, useFlexLayout} from "react-table";
import {Link, useParams} from 'react-router-dom'
import {QueryClient, QueryClientProvider, useQuery} from 'react-query'
import ReactTablePagination from '@/shared/components/table/components/ReactTablePagination';
import {Col, Row} from 'reactstrap';
import {getFirstDay, dateStrToYmd, getReportName} from '@/shared/helpers'
import { StickyContainer, Sticky } from "react-sticky";
import {Loader} from '@/shared/apiHelper.jsx';
import AddIcon from 'mdi-react/AddIcon';
import MinusIcon from 'mdi-react/MinusIcon';
import {toExportRows} from '@/shared/reportExportRows';
import {TABLE_COLUMNS} from "./columns";

import {connect} from "react-redux";
import {
  reducer,
  useEffectToDispatch,
  fetchApiData,
  fetchApiDataExport,
  initialState,
 
  Sorting
} from "@/shared/apiTableHelper"

import { clone} from 'lodash';
import DepositBalanceFilter from "./DepositBalanceFilter";

const queryClient = new QueryClient()

const DataTable = ({organization, programs}) => {
  const { programId } = useParams();

  const [filter, setFilter] = useState({
    programs: programs,
    reportKey: 'sku_value',
    programId: 1,
    from: dateStrToYmd(getFirstDay()),
    to: dateStrToYmd(new Date())
  });
  const isFirstLoad = useRef(true);
  const [useFilter, setUseFilter] = useState(false);
  const [trigger, setTrigger] = useState(0);
  const [exportData, setExportData] = useState([]);
  const [exportHeaders, setExportHeaders] = useState([]);
  const [exportToCsv, setExportToCsv] = useState(false);
  const [isLoader, setLoader] = useState(false);
  const exportLink = React.createRef();
  const [isExporting, setIsExporting] = useState(false);
  const [{queryPageIndex, queryPageSize, totalCount, queryPageFilter, queryPageSortBy, queryTrigger}, dispatch] =
    React.useReducer(reducer, initialState);

  const apiUrl = `/organization/${organization.id}/report/deposit-balance`;
  const apiFilter = useMemo(() => {
    const f = clone(filter);

    if (
      isFirstLoad.current &&
      programs &&
      Array.isArray(filter.programs) &&
      filter.programs.length === programs.length
    ) {
      f.programs = ["all"];
      isFirstLoad.current = false;
    }
    return f;
  }, [filter, programs]);

  const {isLoading, error, data, isSuccess, status, isFetching} = useQuery(
    [['programReportDepositBalance', programId], apiUrl, queryPageIndex, queryPageSize, queryPageFilter, queryPageSortBy, queryTrigger],
    () => fetchApiData(
      {
        url: apiUrl,
        page: queryPageIndex,
        size: queryPageSize,
        filter: apiFilter,
        sortby: queryPageSortBy,
        trigger: queryTrigger
      }
    ),
    {
      keepPreviousData: true,
      staleTime: Infinity,
    }
  );

  useEffect(() => {
    if (exportToCsv) {
      if (exportLink.current) {
        setExportToCsv(false);
        exportLink.current.link.click();
      }
    }
  }, [exportLink])

  const download = async (filterValues) => {
    try {
      setIsExporting(true);
    let tmpFilter = clone(filterValues);
    if (programs && tmpFilter.programs.length === programs.length) {
        tmpFilter.programs = ['all'];
    }
    tmpFilter.exportToCsv = 1;

    const response = await fetchApiDataExport(
      {
        url: apiUrl,
        filter: tmpFilter,
        sortby: queryPageSortBy,
        trigger: queryTrigger
      }
    );
    setExportData(toExportRows(rows));
    setExportHeaders(response.headers);
    setExportToCsv(true);
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setIsExporting(false);
    }
  }

  let columns = useMemo(
    () => [
      {
        // Build our expander column
        id: "expander", // Make sure it has an ID
        Header: ({ getToggleAllRowsExpandedProps, isAllRowsExpanded }) => (
          <span {...getToggleAllRowsExpandedProps()}>
            {isAllRowsExpanded ? "Collapse All" : "Expand All"}
          </span>
        ),
        Cell: ({ row }) => {
          return row.canExpand ? (
            <span
              {...row.getToggleRowExpandedProps({
                style: {
                  paddingLeft: `${row.depth * 2}rem`,
                  cursor: "pointer",
                },
              })}
            >
              {row.isExpanded ? (
                <MinusIcon className="chevron-expand" />
              ) : (
                <AddIcon className="chevron-expand" />
              )}
            </span>
          ) : null;
        },
        width: 125,
      },
      ...TABLE_COLUMNS
    ],
    []
  );

  const totalPageCount = Math.ceil(totalCount / queryPageSize)

  const defaultColumn = React.useMemo(
    () => ({
      // minWidth: 100,
      // width: 150,
      // maxWidth: 120,
    }),
    []
  )

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    footerGroups,
    rows,
    prepareRow,
    rowSpanHeaders,
    page,
    pageCount,
    pageOptions,
    gotoPage,
    previousPage,
    canPreviousPage,
    nextPage,
    canNextPage,
    setPageSize,
    state: {pageIndex, pageSize, sortBy}
  } = useTable({
      columns: columns,
      data: data ? Object.values(data.results) : [],
      initialState: {
        pageIndex: queryPageIndex,
        pageSize: queryPageSize,
        sortBy: queryPageSortBy,
      },
      manualPagination: true, // Tell the usePagination
      pageCount: data ? totalPageCount : null,
      autoResetSortBy: false,
      autoResetExpanded: false,
      autoResetPage: false,
      disableResizing: true,
      autoResetHiddenColumns: false,
      striped: true,
      defaultColumn,
    },
    useSortBy,
    useExpanded,
    usePagination,
    useResizeColumns,
    useFlexLayout,
  );

  const manualPageSize = []
  useEffectToDispatch(dispatch, {pageIndex, pageSize, gotoPage, sortBy, filter, data, useFilter, trigger});

  if (isLoading) {
    return <p>Loading...</p>;
  }

  if (error) {
    return <p>Error: {JSON.stringify(error)}</p>;
  }

  if (isSuccess)
    return (
      <StickyContainer>
        <div className='table react-table report-table'>
          <div className="action-panel">
            <Row className="mx-0">
              <Col>
                <DepositBalanceFilter
                  filter={filter} setFilter={setFilter} useFilter={useFilter} setUseFilter={setUseFilter}
                  exportData={exportData} exportLink={exportLink} exportHeaders={exportHeaders}
                  download={download} isExporting={isExporting} reportName={getReportName("DepositBalance")}/>
              </Col>
            </Row>
          </div>
          {
            isLoading || isFetching && <Loader></Loader>
          }
          {
            isSuccess &&
            <table {...getTableProps()} className="table table-striped report-table">
              <Sticky  topOffset={80}>
                {({ style }) => (
                  <thead style={{...style, top:'60px'}}>
                    {headerGroups.map((headerGroup) => (
                      <tr {...headerGroup.getHeaderGroupProps()}>
                        {headerGroup.headers.map(column => (
                          <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                            {column.render('Header')}
                            {column.isSorted ? <Sorting column={column}/> : ''}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                )}
              </Sticky>
              <tbody className="table table--bordered" {...getTableBodyProps()} >
              {page.map( row => {
                prepareRow(row);
                // console.log(row)
                const subCount = (row.id.match(/\./g) || []).length

                // const paddingCount = subCount > 0 ? Number(subCount) + 3 : 0;
                // console.log(subCount)
                return (
                  <tr {...row.getRowProps()}>
                    {
                      row.cells.map( cell => {
                        // console.log(cell)

                        const paddingLeft = subCount * 20
                        return <td {...cell.getCellProps({className: cell.column.className})}>
                                                            <span style={cell.column.Header==='#' ? {paddingLeft: `${paddingLeft}px`} : null}>{cell.render('Cell')}
                                                            </span>
                        </td>
                      })
                    }
                  </tr>
                )
              })}
              </tbody>
              <tfoot>
              {footerGroups.map((footerGroup) => (
                <tr {...footerGroup.getFooterGroupProps()}>
                  {footerGroup.headers.map(column => (
                    <th {...column.getFooterProps()}>{column.render('Footer')}</th>
                  ))}
                </tr>
              ))}
              </tfoot>
            </table>
          }

        </div>
      </StickyContainer>
    )
}

const TableWrapper = ({organization, programs}) => {
  if (!organization || !programs ) return 'Loading...'
  return (
    <QueryClientProvider client={queryClient}>
      <DataTable organization={organization}  programs={programs}/>
    </QueryClientProvider>
  )
}

const mapStateToProps = (state) => {
  return {
    organization: state.organization,
  };
};
export default connect(mapStateToProps)(TableWrapper);